import OriginalQuestion from '../../../api/v2/models/question'
import EditorQuersion from '../../models/Question'
import OriginalAnswerItem from '../../../api/v2/models/answer_item'
import EditorAnswerItem from '../../../../common/AnswerItem'
import AnswerItemComparison from './AnswerItemComparison'
import ChangeLog, { QuestionChangeLog, AnswerChangeLog } from './ChangeLog'
import { CHANGE_TYPE, QuestionForm, AnswerForm, objToSkillForms } from '../../../api/v2/forms/exam_comparison'
import questionParser from '../../../../helpers/question-parser'
import SkillComparison from './SkillComparison'

export default class QuestionComparison {
  readonly isSameType: boolean = false
  readonly isSameExplanation: boolean = false
  readonly isSameGroup: boolean = false
  readonly isSameBody: boolean = false
  readonly answerItemComparisons: AnswerItemComparison[] = []
  readonly skillComparison: SkillComparison

  constructor(readonly originalQuestion: OriginalQuestion | null, readonly editorQuersion: EditorQuersion | null) {
    if (!originalQuestion) {
      // アンサー追加された
      for (let index = 0; index < this.editorQuersion.answerItems.length; index++) {
        const answerItemComparison = this.compareAnswerItem(null, this.editorQuersion.answerItems[index])
        this.answerItemComparisons.push(answerItemComparison)
      }

      this.skillComparison = new SkillComparison(null, null, this.editorQuersion.skills)
      return
    }
    if (!editorQuersion) {
      // アンサー削除された
      for (let index = 0; index < this.originalQuestion.answerItems.length; index++) {
        const answerItemComparison = this.compareAnswerItem(originalQuestion.answerItems[index], null)
        this.answerItemComparisons.push(answerItemComparison)
      }
      this.skillComparison = new SkillComparison(this.originalQuestion.skills, this.originalQuestion.skillItems, null)
      return
    }

    this.isSameType = originalQuestion.fieldType == editorQuersion.fieldType
    this.isSameExplanation = originalQuestion.explanation == editorQuersion.description
    this.isSameBody = originalQuestion.body == editorQuersion.questionBody
    this.isSameGroup = originalQuestion.group == editorQuersion.group

    this.skillComparison = new SkillComparison(
      this.originalQuestion.skills,
      this.originalQuestion.skillItems,
      this.editorQuersion.skills
    )

    const originalLength = originalQuestion.answerItems.length
    const editorLength = editorQuersion.answerItems.length
    const minLength = originalLength < editorLength ? originalLength : editorLength
    this.answerItemComparisons = []
    for (let index = 0; index < minLength; index++) {
      const answerItemComparison = this.compareAnswerItem(
        originalQuestion.answerItems[index],
        editorQuersion.answerItems[index]
      )
      this.answerItemComparisons.push(answerItemComparison)
    }

    // アンサー削除された
    if (originalLength > minLength) {
      for (let index = minLength; index < this.originalQuestion.answerItems.length; index++) {
        const answerItemComparison = this.compareAnswerItem(originalQuestion.answerItems[index], null)
        this.answerItemComparisons.push(answerItemComparison)
      }
    }

    // アンサー追加された
    if (editorLength > minLength) {
      for (let index = minLength; index < this.editorQuersion.answerItems.length; index++) {
        const answerItemComparison = this.compareAnswerItem(null, this.editorQuersion.answerItems[index])
        this.answerItemComparisons.push(answerItemComparison)
      }
    }
  }

  private compareAnswerItem(
    originalAnswerItem: OriginalAnswerItem | null,
    editorAnswerItem: EditorAnswerItem | null
  ): AnswerItemComparison {
    return new AnswerItemComparison(originalAnswerItem, editorAnswerItem)
  }

  get isSame() {
    return this.isSameExplanation && this.isSameType && this.isSameGroup && this.isSameBody && this.skillComparison.isSame
  }

  get questionChangeLogs(): QuestionChangeLog[] {
    const questionChangeLogs = []
    if (!this.originalQuestion) {
      questionChangeLogs.push(
        new QuestionChangeLog(CHANGE_TYPE.Create, '質問の追加', '', this.editorQuersion.fieldType)
      )
      return questionChangeLogs
    }
    if (!this.editorQuersion) {
      questionChangeLogs.push(
        new QuestionChangeLog(CHANGE_TYPE.Create, '質問の削除', this.originalQuestion.fieldType, '')
      )
      return questionChangeLogs
    }

    if (!this.skillComparison.isSame) {
      questionChangeLogs.push(new QuestionChangeLog(CHANGE_TYPE.Update, 'スキール更新', '', ''))
    }

    if (!this.isSameType) {
      questionChangeLogs.push(
        new QuestionChangeLog(
          CHANGE_TYPE.Update,
          '質問のタイプ',
          this.originalQuestion.fieldType,
          this.editorQuersion.fieldType
        )
      )
    }

    if (!this.isSameGroup) {
      questionChangeLogs.push(
        new QuestionChangeLog(
          CHANGE_TYPE.Update,
          '質問のグループ',
          this.originalQuestion.group.toString(),
          this.editorQuersion.group.toString()
        )
      )
    }

    if (!this.isSameExplanation) {
      questionChangeLogs.push(
        new QuestionChangeLog(
          CHANGE_TYPE.Update,
          '質問の解読',
          this.originalQuestion.explanation,
          this.editorQuersion.description
        )
      )
    }
    return questionChangeLogs
  }

  get answerChangeLogs(): AnswerChangeLog[] {
    let answerChangeLogs = []
    if (!this.isSameBody) {
      this.answerItemComparisons
        .filter((item) => !item.isSame)
        .forEach((item) => {
          answerChangeLogs = [...answerChangeLogs, ...item.changeLogs]
        })
    }
    return answerChangeLogs
  }

  get changeLogs(): ChangeLog[] {
    return [...this.questionChangeLogs, ...this.answerChangeLogs]
  }

  get form(): QuestionForm | null {
    const form = new QuestionForm()
    if (!this.editorQuersion) {
      form.changeType = CHANGE_TYPE.Delete
      form.id = this.originalQuestion.id
      form.answerItemsAttributes = this.answerItemComparisons.map((element) => element.form)
      return form
    }

    form.body = this.editorQuersion.questionBody
    form.explanation = this.editorQuersion.description
    form.fieldType = this.editorQuersion.fieldType
    form.group = this.editorQuersion.group
    form.htmlBody = questionParser(this.editorQuersion.questionBody, this.editorQuersion.fieldType, false)
    form.skills = objToSkillForms(this.editorQuersion.skills)
    form.answerItemsAttributes = this.answerItemComparisons
      .filter((item) => !item.isSame)
      .map((element) => element.form)
    if (this.originalQuestion && !this.isSame) {
      form.id = this.originalQuestion.id
      form.changeType = CHANGE_TYPE.Update
    } else if (this.originalQuestion && this.isSame) {
      form.id = this.originalQuestion.id
      form.changeType = CHANGE_TYPE.None
    }
    else {
      form.changeType = CHANGE_TYPE.Create
    }

    return form
  }
}
